import { useState, useEffect } from 'react';
import { t } from '@lingui/core/macro';
import HeaderActions from './HeaderActions';
import AddLinkModal from './AddLinkModal';
import FolderEditModal from './FolderEditModal/FolderEditModal';
import TagEditModal from './TagEditModal/TagEditModal';
import { ViewMode, Folder, Tag } from '../types/api';
import BatchOperationsBar from './BatchOperationsBar';
import ButtonIcon from './ui/ButtonIcon';
import Icon from './ui/Icon';
import ThemeLogo from './ui/ThemeLogo';
import { useMobileNavSidebar } from '../hooks/useUserPreferences';
import { useCurrentUser } from '../services/userHooks';
import { useBookmarkSelection } from '../contexts/BookmarkSelectionContext';
import { getPageInfo } from '../utils/pageInfo';
import { formatCount } from '../utils/numberUtils';

interface PageHeaderProps {
  mode?: ViewMode;
  folderTitle?: string;
  folderId?: string;
  tagSlug?: string;
  currentFolder?: Folder;
  currentTag?: Tag;
}

function PageHeader({
  mode = 'unread',
  folderTitle,
  folderId,
  tagSlug,
  currentFolder,
  currentTag,
}: PageHeaderProps) {
  const [addLinkModalOpen, setAddLinkModalOpen] = useState(false);
  const [folderEditModalOpen, setFolderEditModalOpen] = useState(false);
  const [tagEditModalOpen, setTagEditModalOpen] = useState(false);
  const { toggleMobileNavSidebar } = useMobileNavSidebar();
  const { currentUser } = useCurrentUser();
  const { isBatchMode } = useBookmarkSelection();

  const handleMobileMenuClick = () => {
    toggleMobileNavSidebar();
  };

  const pageInfo = getPageInfo(
    mode,
    currentUser,
    currentFolder,
    currentTag,
    folderTitle,
    tagSlug,
  );
  // Mobile title uses the label from pageInfo, except for home/unread which shows logo
  const mobileTitle =
    pageInfo && mode !== 'home' && mode !== 'unread' ? pageInfo.label : null;

  // Handle display of modal
  const handleAddLink = () => {
    setAddLinkModalOpen(true);
  };

  const handleEditFolder = () => {
    setFolderEditModalOpen(true);
  };

  const handleEditTag = () => {
    setTagEditModalOpen(true);
  };

  // Keyboard shortcut for opening add link modal (Cmd/Ctrl + K).
  // If you change this binding, also update src/components/KeyboardShortcutsModal/shortcuts.ts.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setAddLinkModalOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="page-header-container">
      {/* Mobile-only page header */}
      <div className="page-header page-header-mobile" data-testid="page-header-mobile">
        <ButtonIcon
          icon="hamburger"
          title={t`Menu`}
          onClick={handleMobileMenuClick}
          className="control-bar-item"
        />

        <div className="flex-1 text-center">
          {mobileTitle ? (
            <span className="text-base font-medium">{mobileTitle}</span>
          ) : (
            <ThemeLogo className="mx-auto h-6" />
          )}
        </div>

        <div className="w-10 text-right"></div>
      </div>

      {/* Desktop-only page header */}
      <div
        className="page-header page-header-desktop gap-x-6"
        data-testid="page-header-desktop"
      >
        <div className="flex-1">
          {isBatchMode ? (
            <BatchOperationsBar viewMode={mode} />
          ) : (
            pageInfo && (
              <div className="flex items-center gap-2">
                <Icon name={pageInfo.icon} />
                <div className="flex items-baseline gap-2">
                  <span className="text-copy-primary text-base font-medium">
                    {pageInfo.label}
                  </span>
                  {typeof pageInfo.count === 'number' && pageInfo.count > 0 && (
                    <span
                      className="text-copy-tertiary text-sm"
                      title={String(pageInfo.count)}
                    >
                      {formatCount(pageInfo.count)}
                    </span>
                  )}
                </div>
              </div>
            )
          )}
        </div>

        <HeaderActions
          onAddLink={handleAddLink}
          onEditFolder={handleEditFolder}
          onEditTag={handleEditTag}
          mode={mode}
          folderId={folderId}
          tagSlug={tagSlug}
          isSubscriber={currentUser?.isSubscriber}
          articleCount={pageInfo?.count ?? undefined}
        />
      </div>

      {/* Add Link Modal */}
      <AddLinkModal
        isOpen={addLinkModalOpen}
        onClose={() => setAddLinkModalOpen(false)}
      />

      {/* Folder Edit Modal */}
      {currentFolder && (
        <FolderEditModal
          isOpen={folderEditModalOpen}
          onClose={() => setFolderEditModalOpen(false)}
          folder={currentFolder}
        />
      )}

      {/* Tag Edit Modal */}
      {currentTag && (
        <TagEditModal
          isOpen={tagEditModalOpen}
          onClose={() => setTagEditModalOpen(false)}
          tag={currentTag}
        />
      )}
    </div>
  );
}

export default PageHeader;
